/** @jsx jsx */
import { useEffect, useState } from 'react';
import { jsx } from '@emotion/core';
import { Grid, Hidden, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ChallengePhotoStatusType, LoadingStatusType } from 'interfaces';
import { RESULT_STATUS } from 'constants/challenges';
import PhotoRank from '../PhotoRank';
import Description from './Description';
import * as S from './styles';
import { challengeResultsSelector } from 'reducers/challenge.reducer';
import { IResult } from 'interfaces/challenge.types';
import { userInfosSelector } from 'reducers/app.reducer';
import { LOADING_STATUS } from 'constants/index';

const { cssAlignContent, cssDescriptionContainer, cssSecondary } = S;

type FullDescriptionProps = {
  currentUserVotes: number | null;
  fullUri: string;
  hasChallengeEnded: boolean;
  hasChallengeStarted: boolean;
  resultsLoadingStatus: LoadingStatusType | undefined;
  status: ChallengePhotoStatusType | undefined;
  totalParticipatingUsers: number | null;
};

const FullDescription = ({
  currentUserVotes,
  fullUri,
  hasChallengeEnded,
  hasChallengeStarted,
  resultsLoadingStatus,
  status,
  totalParticipatingUsers,
}: FullDescriptionProps): JSX.Element => {
  const { t } = useTranslation();
  const { id: challengeId }: { id: string } = useParams();

  // In order to have a dynamic ranking based on dynamic votes
  const [rank, setRank] = useState<number | undefined>(undefined);
  const { id: userId } = useSelector(userInfosSelector);
  const { data: challengeResults } = useSelector(
    challengeResultsSelector(Number(challengeId)),
  );

  useEffect(() => {
    if (challengeResults) {
      const userResult: IResult | undefined = Object.values(
        challengeResults?.byId,
      ).find((value) => {
        return value.targetId === userId;
      });
      setRank(userResult?.rank);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeResults]);

  const getBasicDescription = (
    texts: [string, string, string?],
    showRecommendations = false,
  ): JSX.Element => (
    <Grid item xs={12} md={9} css={cssAlignContent}>
      <Description
        resultsLoadingStatus={resultsLoadingStatus}
        showRecommendations={showRecommendations}
        texts={texts}
      />
    </Grid>
  );

  if (!hasChallengeStarted) {
    return getBasicDescription([
      t('photo_contest.results.before_open.subtitle'),
      t('photo_contest.results.before_open.description'),
    ]);
  }

  if (hasChallengeEnded && !fullUri) {
    return getBasicDescription([
      t('photo_contest.results.no_participation.subtitle'),
      t('photo_contest.results.no_participation.description1'),
      t('photo_contest.results.no_participation.description2'),
    ]);
  }

  if (status == null) {
    return getBasicDescription(
      [
        t('photo_contest.results.undefined_status.subtitle'),
        t('photo_contest.results.undefined_status.description'),
      ],
      true,
    );
  }

  if (status === RESULT_STATUS.WAITING) {
    return getBasicDescription([
      t('photo_contest.results.waiting_status.subtitle'),
      t('photo_contest.results.waiting_status.description'),
    ]);
  }

  if (status === RESULT_STATUS.REFUSED) {
    return getBasicDescription([
      t('photo_contest.results.refused_status.subtitle'),
      t('photo_contest.results.refused_status.description'),
    ]);
  }

  let noParticipants = '';
  let withVotesDescription = t(
    'photo_contest.results.validated_status.with_votes_yet.description',
  );
  let withNoVotesDescription = t(
    'photo_contest.results.validated_status.no_votes_yet.description',
  );

  if (!currentUserVotes && hasChallengeEnded) {
    withVotesDescription = t(
      'photo_contest.results.validated_status.with_votes.description',
    );
    withNoVotesDescription = t(
      'photo_contest.results.validated_status.no_votes.description',
    );
  }

  if (!totalParticipatingUsers) {
    noParticipants = hasChallengeEnded
      ? t('photo_contest.results.validated_status.no_participants')
      : t('photo_contest.results.validated_status.no_participants_yet');
  }

  return (
    <Grid item xs={12} md={9} css={cssDescriptionContainer}>
      <Grid container spacing={1}>
        {/* withVotes : Your photo has already collected N votes
         * || Your photo has collected N votes
         */}
        {/* noVotes: You do not have a vote yet || You do not have a vote */}
        <Grid item xs={6} css={cssAlignContent}>
          {currentUserVotes && currentUserVotes > 0 ? (
            <Description
              resultsLoadingStatus={resultsLoadingStatus}
              showVoteDescription
              texts={[
                t('photo_contest.results.validated_status.with_votes.subtitle'),
                withVotesDescription,
              ]}
              votes={currentUserVotes}
            />
          ) : (
            <Description
              resultsLoadingStatus={resultsLoadingStatus}
              showVoteDescription
              texts={[
                t('photo_contest.results.validated_status.no_votes.subtitle'),
                withNoVotesDescription,
              ]}
            />
          )}
        </Grid>

        {/* Temporary Rank || Rank */}
        {resultsLoadingStatus !== LOADING_STATUS.PENDING && (
          <Grid item xs={6} css={cssAlignContent}>
            <Description
              resultsLoadingStatus={resultsLoadingStatus}
              texts={[
                hasChallengeEnded
                  ? t('photo_contest.results.validated_status.final_rank_title')
                  : t(
                      'photo_contest.results.validated_status.temporary_rank_title',
                    ),
                noParticipants,
              ]}
            />
            {totalParticipatingUsers && (
              <S.Wrapper>
                <Hidden smDown>
                  <PhotoRank
                    rank={rank || totalParticipatingUsers}
                    totalParticipatingUsers={totalParticipatingUsers}
                    small
                  />
                </Hidden>
                <Hidden mdUp>
                  <Typography variant="body1">
                    <strong>
                      <span css={cssSecondary}>
                        {rank || totalParticipatingUsers}
                        <sup>e</sup>
                      </span>
                      <small>
                        <small>/{totalParticipatingUsers}</small>
                      </small>
                    </strong>
                  </Typography>
                </Hidden>
              </S.Wrapper>
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default FullDescription;
